import { graphql } from 'app/generated/gql'

/**
 * Shared GraphQL operations for the Tasks UI.
 *
 * Single source of truth for the Task field set, so the inbox view, the table
 * view, the create modals and the activity feed card all stay in sync when a
 * field is added or removed.
 *
 * Two shapes are exported because the codebase has two GraphQL surfaces:
 *
 *   - generated graphql() documents (`TASK_FIELDS_FRAGMENT`, `TASK_QUERY`, etc.) for
 *     urql `useQuery` / `useMutation` hooks and direct `client.mutation`
 *     calls. These are `DocumentNode`s and benefit from urql's parser cache.
 *
 *   - Plain `TASK_FIELDS` string for `gqlFetchOne` / `gqlFetchList`, the
 *     non-hook helpers in `app/lib/graphqlClient`. Those helpers build a
 *     query string at call-time so they need a raw field selection.
 *
 * `__typename` is included explicitly. urql's document `cacheExchange` adds it
 * automatically, but writing it out documents intent and is required if we
 * ever swap in `@urql/exchange-graphcache` for normalized caching.
 */

export const TASK_FIELDS = `
  __typename
  contactId
  createdById
  createdDate
  description
  dueDate
  id
  locationId
  ownerId
  roleId
  completedByUserId
  status
  title
  updatedDate
  version
  workspaceId
`

export const TASK_FIELDS_FRAGMENT = graphql(`
  fragment TaskFields on Task {
    __typename
    contactId
    createdById
    createdDate
    description
    dueDate
    id
    locationId
    ownerId
    roleId
    completedByUserId
    status
    title
    updatedDate
    version
    workspaceId
  }
`)

export const TASK_QUERY = graphql(`
  query Task($id: ID!) {
    task(id: $id) {
      ...TaskFields
    }
  }
`)

// Narrow set of fields — the wider user(id:) selection currently trips an
// INTERNAL_SERVER_ERROR for users with a null role, which nullifies the whole
// user object. {id name email} is enough for the drawer's Owner row.
export const TASK_OWNER_QUERY = graphql(`
  query TaskOwner($id: ID!) {
    user(id: $id) {
      __typename
      id
      name
      email
    }
  }
`)

export const CREATE_TASK_MUTATION = graphql(`
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(input: $input) {
      ...TaskFields
    }
  }
`)

export const UPDATE_TASK_MUTATION = graphql(`
  mutation UpdateTask($id: ID!, $input: UpdateTaskInput!) {
    updateTask(id: $id, input: $input) {
      ...TaskFields
    }
  }
`)

export const DELETE_TASK_MUTATION = graphql(`
  mutation DeleteTask($id: ID!) {
    deleteTask(id: $id)
  }
`)
